"use client";

import { useMarket } from "@features/market/useMarket";
import { addToCartEvent } from "@features/tracking/gtm/addToCartEvent";
import { sendVoyadoCartChangeEvent } from "@features/tracking/voyado-tracking";
import type { Cart } from "@repo/core/cart";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CartProduct } from "@features/product/utils/toCartProduct";
import {
  cancelCartQuery,
  setCartQueryData,
  syncCartQueries,
} from "../config/cache";
import { cartKeys } from "../config/keys";
import addToCart from "../services/addToCart";
import addToCartOptimistically from "../services/addToCartOptimistically";

export function useAddToCart() {
  const market = useMarket();
  const queryClient = useQueryClient();
  const locale = market.locale;

  return useMutation({
    mutationFn: (input: CartProduct[]) => addToCart(input, market),
    onMutate: async (input) => {
      await cancelCartQuery(queryClient);
      const previousCart = queryClient.getQueryData<Cart>(
        cartKeys.cart(locale),
      );

      setCartQueryData(
        queryClient,
        locale,
        addToCartOptimistically(input, market, previousCart),
      );

      return { previousCart };
    },
    onSuccess: (cart, input) => {
      setCartQueryData(queryClient, locale, cart);

      void addToCartEvent({
        items: input,
        currency: market.currency,
      });

      void sendVoyadoCartChangeEvent({
        locale: market.contentfulLocale,
        cart,
      });
    },
    onError: (_error, _input, context) => {
      setCartQueryData(queryClient, locale, context?.previousCart ?? null);
    },
    onSettled: () => {
      syncCartQueries(queryClient);
    },
  });
}

export default useAddToCart;
